import React, { useEffect, useState } from "react";
import { ShieldCheck, Users, Building2, RefreshCw, UserPlus, KeyRound, Trash2, X, Crown, Layers, CheckCircle2, Clock, XCircle, RotateCcw } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Navigate } from "react-router-dom";

export default function Admin() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [workspaces, setWorkspaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);   // user object

  const refresh = async () => {
    setLoading(true);
    setErr(null);
    try {
      const [s, u, w] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/users"),
        api.get("/admin/workspaces"),
      ]);
      setStats(s.data);
      setUsers(u.data);
      setWorkspaces(w.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Failed to load admin data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_global_admin) return <Navigate to="/home" replace />;

  const toggleAdmin = async (u) => {
    if (!window.confirm(
      `${u.is_global_admin ? "Revoke" : "Grant"} Global Admin for ${u.email}?`
    )) return;
    try {
      await api.patch(`/admin/users/${u.id}`, {
        is_global_admin: !u.is_global_admin,
      });
      await refresh();
    } catch (e) {
      alert(e?.response?.data?.detail || "Update failed");
    }
  };
  const toggleActive = async (u) => {
    if (u.id === user.id && u.is_active) {
      alert("You cannot deactivate your own account.");
      return;
    }
    if (!window.confirm(
      `${u.is_active ? "Deactivate" : "Reactivate"} ${u.email}?`
    )) return;
    try {
      await api.patch(`/admin/users/${u.id}`, { is_active: !u.is_active });
      await refresh();
    } catch (e) {
      alert(e?.response?.data?.detail || "Update failed");
    }
  };

  const toggleTier = async (u) => {
    const currentTier = u.tier || "basic";
    const nextTier = currentTier === "premium" ? "basic" : "premium";
    if (!window.confirm(
      `${nextTier === "premium" ? "Grant" : "Revoke"} Premium Plan access for ${u.email}?`
    )) return;
    try {
      await api.patch(`/admin/users/${u.id}`, { tier: nextTier });
      await refresh();
    } catch (e) {
      alert(e?.response?.data?.detail || "Update failed");
    }
  };

  const approveUser = async (u) => {
    if (!window.confirm(`Approve ${u.email}? They'll be able to log in immediately.`)) return;
    try {
      await api.post(`/admin/users/${u.id}/approve`);
      await refresh();
    } catch (e) {
      alert(e?.response?.data?.detail || "Approval failed");
    }
  };

  const declineUser = async (u) => {
    if (!window.confirm(
      `Decline ${u.email}? They will not be able to log in.\n\nThey can be re-activated later from the Declined Users panel.`,
    )) return;
    try {
      await api.post(`/admin/users/${u.id}/decline`);
      await refresh();
    } catch (e) {
      alert(e?.response?.data?.detail || "Decline failed");
    }
  };

  const deleteUser = async (u) => {
    if (u.id === user.id) {
      alert("You cannot delete your own account.");
      return;
    }
    if (!window.confirm(
      `Permanently delete ${u.email}?\n\n` +
      `• Their owned workspaces (where they are the only member) and ALL associated projects, tasks, F&B profiles and notifications will be wiped.\n` +
      `• Where they share a workspace with others, they will simply be removed from the member list.\n\n` +
      `This action cannot be undone.`
    )) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      await refresh();
    } catch (e) {
      alert(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="px-6 py-6 max-w-7xl mx-auto" data-testid="admin-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-0.5">
            Vorion HQ · Iteration 27
          </div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-[#D4A968]" /> Global Admin Panel
          </h1>
          <p className="text-sm text-zinc-400">
            Platform-wide visibility. Cannot read individual workspace data — only metadata.
          </p>
        </div>
        <button
          type="button"
          onClick={refresh}
          disabled={loading}
          data-testid="admin-refresh-btn"
          className="text-[10px] font-mono uppercase tracking-widest px-3 py-1.5 rounded bg-[#D4A968]/15 border border-[#D4A968]/40 text-[#D4A968] hover:bg-[#D4A968]/25 disabled:opacity-40 inline-flex items-center gap-1.5"
        >
          <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      {err && (
        <div className="mb-4 bg-[#EF4444]/10 border border-[#EF4444]/30 text-[#FCA5A5] rounded p-3 text-sm" data-testid="admin-error">
          {err}
        </div>
      )}

      {/* KPI strip */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-6" data-testid="admin-stats">
          {[
            { label: "Users", value: stats.users, testid: "stat-users" },
            { label: "Workspaces", value: stats.workspaces, testid: "stat-workspaces" },
            { label: "Projects", value: stats.projects, testid: "stat-projects" },
            { label: "Tasks", value: stats.tasks, testid: "stat-tasks" },
            { label: "Global Admins", value: stats.global_admins, testid: "stat-global-admins" },
          ].map((k) => (
            <div key={k.label} className="bg-[#141417] border border-zinc-800 rounded p-3" data-testid={k.testid}>
              <div className="text-[9px] font-mono uppercase tracking-widest text-zinc-500">{k.label}</div>
              <div className="text-2xl font-semibold text-[#D4A968] tabular-nums mt-0.5">{k.value}</div>
            </div>
          ))}
        </div>
      )}

      {/* User segmentation */}
      {(() => {
        const pendingUsers   = users.filter((u) => (u.approval_status || "approved") === "pending");
        const activeUsers    = users.filter((u) => (u.approval_status || "approved") === "approved");
        const declinedUsers  = users.filter((u) => (u.approval_status || "approved") === "declined");

        const renderUserRow = (u, variant) => (
          <tr key={u.id} className="border-t border-zinc-800/70 hover:bg-[#D4A968]/5" data-testid={`admin-user-row-${u.email}`}>
            <td className="px-3 py-2 text-zinc-100">{u.name}</td>
            <td className="px-3 py-2 text-zinc-300 font-mono">{u.email}</td>
            <td className="px-3 py-2">
              {(u.tier || "basic") === "premium"
                ? <span className="text-[#D4A968] font-mono text-[10px] uppercase tracking-widest inline-flex items-center gap-1"><Crown className="w-2.5 h-2.5" /> PREMIUM</span>
                : <span className="text-zinc-400 font-mono text-[10px] uppercase tracking-widest">BASIC</span>}
            </td>
            <td className="px-3 py-2 text-zinc-300 tabular-nums">{u.workspace_count}</td>
            <td className="px-3 py-2">
              {u.is_global_admin
                ? <span className="text-[#D4A968] font-mono text-[10px] uppercase tracking-widest">GLOBAL</span>
                : <span className="text-zinc-500 font-mono text-[10px] uppercase tracking-widest">—</span>}
            </td>
            <td className="px-3 py-2 text-zinc-500 font-mono text-[10px]">{(u.created_at || "").slice(0,10)}</td>
            <td className="px-3 py-2">
              <div className="flex flex-wrap gap-1.5">
                {variant === "pending" && (
                  <>
                    <button
                      type="button" onClick={() => approveUser(u)}
                      data-testid={`admin-approve-${u.email}`}
                      className="text-[9px] font-mono uppercase tracking-widest px-2 py-1 rounded bg-[#10B981]/15 border border-[#10B981]/40 text-[#86EFAC] hover:bg-[#10B981]/25 inline-flex items-center gap-1"
                    >
                      <CheckCircle2 className="w-2.5 h-2.5" /> Approve
                    </button>
                    <button
                      type="button" onClick={() => declineUser(u)}
                      data-testid={`admin-decline-${u.email}`}
                      className="text-[9px] font-mono uppercase tracking-widest px-2 py-1 rounded bg-[#EF4444]/15 border border-[#EF4444]/40 text-[#FCA5A5] hover:bg-[#EF4444]/25 inline-flex items-center gap-1"
                    >
                      <XCircle className="w-2.5 h-2.5" /> Decline
                    </button>
                  </>
                )}
                {variant === "declined" && (
                  <button
                    type="button" onClick={() => approveUser(u)}
                    data-testid={`admin-reactivate-${u.email}`}
                    className="text-[9px] font-mono uppercase tracking-widest px-2 py-1 rounded bg-[#10B981]/15 border border-[#10B981]/40 text-[#86EFAC] hover:bg-[#10B981]/25 inline-flex items-center gap-1"
                  >
                    <RotateCcw className="w-2.5 h-2.5" /> Re-activate
                  </button>
                )}
                {variant === "active" && (
                  <>
                    <button
                      type="button" onClick={() => toggleTier(u)}
                      data-testid={`admin-toggle-tier-${u.email}`}
                      className={(u.tier === "premium")
                        ? "text-[9px] font-mono uppercase tracking-widest px-2 py-1 rounded bg-[#D4A968]/15 border border-[#D4A968]/40 text-[#D4A968] hover:bg-[#D4A968]/25 inline-flex items-center gap-1"
                        : "text-[9px] font-mono uppercase tracking-widest px-2 py-1 rounded bg-zinc-800 border border-zinc-700 text-zinc-300 hover:text-white inline-flex items-center gap-1"
                      }
                      title={u.tier === "premium" ? "Revoke Premium" : "Grant Premium"}
                    >
                      <Crown className="w-2.5 h-2.5" /> {u.tier === "premium" ? "Make Basic" : "Make Premium"}
                    </button>
                    <button
                      type="button" onClick={() => toggleAdmin(u)}
                      data-testid={`admin-toggle-admin-${u.email}`}
                      className="text-[9px] font-mono uppercase tracking-widest px-2 py-1 rounded bg-[#D4A968]/15 border border-[#D4A968]/40 text-[#D4A968] hover:bg-[#D4A968]/25"
                    >
                      {u.is_global_admin ? "Revoke" : "Promote"}
                    </button>
                    <button
                      type="button" onClick={() => declineUser(u)}
                      data-testid={`admin-disable-${u.email}`}
                      className="text-[9px] font-mono uppercase tracking-widest px-2 py-1 rounded bg-zinc-800 border border-zinc-700 text-zinc-300 hover:text-white inline-flex items-center gap-1"
                      title="Disable login — moves user to Declined panel"
                    >
                      <XCircle className="w-2.5 h-2.5" /> Disable
                    </button>
                  </>
                )}
                <button
                  type="button" onClick={() => setResetTarget(u)}
                  data-testid={`admin-reset-password-${u.email}`}
                  className="text-[9px] font-mono uppercase tracking-widest px-2 py-1 rounded bg-[#38BDF8]/15 border border-[#38BDF8]/40 text-[#7DD3FC] hover:bg-[#38BDF8]/25 inline-flex items-center gap-1"
                  title="Reset password"
                >
                  <KeyRound className="w-2.5 h-2.5" /> Reset
                </button>
                {u.id !== user.id && (
                  <button
                    type="button" onClick={() => deleteUser(u)}
                    data-testid={`admin-delete-${u.email}`}
                    className="text-[9px] font-mono uppercase tracking-widest px-2 py-1 rounded bg-[#EF4444]/15 border border-[#EF4444]/40 text-[#FCA5A5] hover:bg-[#EF4444]/25 inline-flex items-center gap-1"
                    title="Delete user permanently"
                  >
                    <Trash2 className="w-2.5 h-2.5" /> Delete
                  </button>
                )}
              </div>
            </td>
          </tr>
        );

        const tableHeader = (
          <thead className="bg-[#0B0B0E] text-zinc-500">
            <tr>
              {["Name","Email","Plan","Workspaces","Global Admin","Joined","Actions"].map((h) => (
                <th key={h} className="text-left font-mono uppercase tracking-widest px-3 py-1.5 text-[9px]">{h}</th>
              ))}
            </tr>
          </thead>
        );

        return (
          <>
            {/* Pending Approval */}
            <section className="bg-[#141417] border border-[#D4A968]/30 rounded-lg overflow-hidden mb-6" data-testid="admin-pending-users-table">
              <div className="bg-[#D4A968]/10 px-4 py-2.5 flex items-center gap-2 border-b border-[#D4A968]/30">
                <Clock className="w-4 h-4 text-[#D4A968]" />
                <h2 className="text-sm font-semibold text-[#D4A968]">Pending Approval</h2>
                <span className="text-[10px] font-mono uppercase tracking-widest text-[#D4A968]/70">
                  {pendingUsers.length} waiting
                </span>
                <span className="ml-auto text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                  Self-registered users wait here until you Approve or Decline.
                </span>
              </div>
              {pendingUsers.length === 0 ? (
                <div className="px-4 py-6 text-center text-xs text-zinc-500" data-testid="admin-pending-empty">
                  No users waiting for approval.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    {tableHeader}
                    <tbody>{pendingUsers.map((u) => renderUserRow(u, "pending"))}</tbody>
                  </table>
                </div>
              )}
            </section>

            {/* Active Users */}
            <section className="bg-[#141417] border border-zinc-800 rounded-lg overflow-hidden mb-6" data-testid="admin-users-table">
              <div className="bg-[#0F0F12] px-4 py-2.5 flex items-center gap-2 border-b border-zinc-800">
                <Users className="w-4 h-4 text-[#10B981]" />
                <h2 className="text-sm font-semibold text-white">Active Users</h2>
                <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                  {activeUsers.length} active
                </span>
                <button
                  type="button"
                  onClick={() => setCreateOpen(true)}
                  data-testid="admin-new-user-btn"
                  className="ml-auto text-[10px] font-mono uppercase tracking-widest px-3 py-1.5 rounded bg-[#D4A968] hover:bg-[#E8C285] text-black font-semibold inline-flex items-center gap-1.5"
                >
                  <UserPlus className="w-3 h-3" />
                  New User
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  {tableHeader}
                  <tbody>{activeUsers.map((u) => renderUserRow(u, "active"))}</tbody>
                </table>
              </div>
            </section>

            {/* Declined / Disabled Users */}
            <section className="bg-[#141417] border border-[#EF4444]/30 rounded-lg overflow-hidden mb-6" data-testid="admin-declined-users-table">
              <div className="bg-[#EF4444]/10 px-4 py-2.5 flex items-center gap-2 border-b border-[#EF4444]/30">
                <XCircle className="w-4 h-4 text-[#FCA5A5]" />
                <h2 className="text-sm font-semibold text-[#FCA5A5]">Declined / Disabled Users</h2>
                <span className="text-[10px] font-mono uppercase tracking-widest text-[#FCA5A5]/70">
                  {declinedUsers.length} disabled
                </span>
                <span className="ml-auto text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                  Cannot log in. Re-activate to restore access, or Delete permanently.
                </span>
              </div>
              {declinedUsers.length === 0 ? (
                <div className="px-4 py-6 text-center text-xs text-zinc-500" data-testid="admin-declined-empty">
                  No declined or disabled users.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    {tableHeader}
                    <tbody>{declinedUsers.map((u) => renderUserRow(u, "declined"))}</tbody>
                  </table>
                </div>
              )}
            </section>
          </>
        );
      })()}

      {/* Single shared workspace — read-only info card */}
      <section className="bg-[#141417] border border-zinc-800 rounded-lg overflow-hidden" data-testid="admin-workspaces-table">
        <div className="bg-[#0F0F12] px-4 py-2.5 flex items-center gap-2 border-b border-zinc-800">
          <Building2 className="w-4 h-4 text-[#D4A968]" />
          <h2 className="text-sm font-semibold text-white">Workspace</h2>
          <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 ml-3">
            Single shared workspace — creation disabled
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-[#0B0B0E] text-zinc-500">
              <tr>
                {["Name", "Members", "Projects", "Tasks", "Created"].map((h) => (
                  <th key={h} className="text-left font-mono uppercase tracking-widest px-3 py-1.5 text-[9px]">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {workspaces.map((w) => (
                <tr key={w.id} className="border-t border-zinc-800/70" data-testid={`admin-ws-row-${w.slug}`}>
                  <td className="px-3 py-2 text-zinc-100 inline-flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#10B981]" />
                    {w.name}
                  </td>
                  <td className="px-3 py-2 text-zinc-300 tabular-nums">{w.member_count}</td>
                  <td className="px-3 py-2 text-zinc-300 tabular-nums">{w.project_count}</td>
                  <td className="px-3 py-2 text-zinc-300 tabular-nums">{w.task_count}</td>
                  <td className="px-3 py-2 text-zinc-500 font-mono text-[10px]">{(w.created_at || "").slice(0,10)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Plan tiers — Basic & Premium */}
      <section className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-6" data-testid="admin-plans-section">
        <PlanCard
          tier="basic"
          title="Basic Plan"
          tagline="Default plan for every new user"
          users={users}
          features={[
            "Outlet Blueprint (I → IV — Character, Operations, Business Details, SWOT)",
            "Demand Calendar — single outlet view, current year",
            "Focus Revenue per Month — view-only",
            "Trends & Issues — Current Trends + Critical Issues",
            "Up to 1 workspace, 50 tasks total",
            "Email-based notifications (digest only)",
          ]}
        />
        <PlanCard
          tier="premium"
          title="Premium Plan"
          tagline="Full feature set — assigned by Global Admin only"
          users={users}
          features={[
            "Everything in Basic, plus:",
            "Occupancy & RevPASH — 18-column Budget vs Actual, 12-month scope",
            "Analytics tab — 7 donut groups × every meal period",
            "Opportunity Dashboard — Annual Gap Engine + auto-sync to Kanban",
            "Hotel Website daily scraper + AI Assistant grounded mode",
            "Unlimited workspaces, projects, tasks, audits & exports",
            "Real-time notifications + custom audit templates",
          ]}
          highlighted
        />
      </section>

      {createOpen && (
        <CreateUserModal
          onClose={() => setCreateOpen(false)}
          onCreated={() => { setCreateOpen(false); refresh(); }}
        />
      )}
      {resetTarget && (
        <ResetPasswordModal
          target={resetTarget}
          onClose={() => setResetTarget(null)}
          onDone={() => { setResetTarget(null); refresh(); }}
        />
      )}
    </div>
  );
}


function CreateUserModal({ onClose, onCreated }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [tier, setTier] = useState("basic");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post("/admin/users", {
        name: name.trim(),
        email: email.trim().toLowerCase(),
        password,
        is_global_admin: isAdmin,
        tier,
      });
      onCreated();
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || "Create failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalShell title="Create new user" testid="admin-create-user-modal" onClose={onClose}>
      <form onSubmit={submit} className="space-y-3" data-testid="admin-create-user-form">
        <Field label="Full name" required>
          <input
            type="text" value={name} onChange={(e) => setName(e.target.value)}
            data-testid="create-user-name" required minLength={1} maxLength={80}
            className="modal-input"
          />
        </Field>
        <Field label="Email" required>
          <input
            type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            data-testid="create-user-email" required
            className="modal-input"
          />
        </Field>
        <Field label="Temporary password" required hint="At least 6 characters. User can change it later.">
          <input
            type="text" value={password} onChange={(e) => setPassword(e.target.value)}
            data-testid="create-user-password" required minLength={6} maxLength={128}
            className="modal-input"
          />
        </Field>
        <div className="text-[10px] text-zinc-500 bg-[#0B0B0E] border border-zinc-800 rounded px-3 py-2 leading-relaxed">
          New users automatically join the <span className="text-[#D4A968] font-mono">Vorion</span> workspace. Workspace creation is disabled platform-wide.
        </div>
        <Field label="Plan" hint="Defaults to Basic. Premium unlocks the full feature set.">
          <div className="grid grid-cols-2 gap-2">
            {[
              { id: "basic",   label: "Basic",   testid: "create-user-tier-basic" },
              { id: "premium", label: "Premium", testid: "create-user-tier-premium" },
            ].map((t) => {
              const active = tier === t.id;
              return (
                <button
                  type="button"
                  key={t.id}
                  onClick={() => setTier(t.id)}
                  data-testid={t.testid}
                  className={active
                    ? "text-[11px] font-mono uppercase tracking-widest py-2 rounded bg-[#D4A968]/15 border border-[#D4A968]/40 text-[#D4A968] inline-flex items-center justify-center gap-1.5"
                    : "text-[11px] font-mono uppercase tracking-widest py-2 rounded bg-zinc-900 border border-zinc-700 text-zinc-400 hover:text-white"
                  }
                >
                  {t.id === "premium" && <Crown className="w-3 h-3" />}
                  {t.label}
                </button>
              );
            })}
          </div>
        </Field>
        <label className="flex items-center gap-2 text-xs text-zinc-300 cursor-pointer">
          <input
            type="checkbox" checked={isAdmin} onChange={(e) => setIsAdmin(e.target.checked)}
            data-testid="create-user-is-admin" className="accent-[#D4A968]"
          />
          Grant <span className="text-[#D4A968] font-semibold">Global Admin</span> privileges
        </label>
        {error && (
          <div className="text-[#FCA5A5] bg-[#EF4444]/10 border border-[#EF4444]/30 rounded px-2 py-1.5 text-xs" data-testid="create-user-error">
            {error}
          </div>
        )}
        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button" onClick={onClose} disabled={busy}
            className="text-xs px-3 py-2 rounded bg-zinc-800 border border-zinc-700 text-zinc-300 hover:text-white"
          >Cancel</button>
          <button
            type="submit" disabled={busy}
            data-testid="create-user-submit"
            className="text-xs px-3 py-2 rounded bg-[#D4A968] hover:bg-[#E8C285] text-black font-semibold disabled:opacity-50"
          >{busy ? "Creating…" : "Create user"}</button>
        </div>
      </form>
    </ModalShell>
  );
}


function ResetPasswordModal({ target, onClose, onDone }) {
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.patch(`/admin/users/${target.id}`, { password });
      alert(`Password updated for ${target.email}.\n\nNew password: ${password}\n\nMake sure to share it securely.`);
      onDone();
    } catch (err) {
      setError(err?.response?.data?.detail || "Reset failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalShell title={`Reset password — ${target.email}`} testid="admin-reset-password-modal" onClose={onClose}>
      <form onSubmit={submit} className="space-y-3" data-testid="admin-reset-password-form">
        <Field label="New password" required hint="Minimum 6 characters. The user will need to be given this password.">
          <input
            type="text" value={password} onChange={(e) => setPassword(e.target.value)}
            data-testid="reset-password-input" required minLength={6} maxLength={128} autoFocus
            className="modal-input"
          />
        </Field>
        {error && (
          <div className="text-[#FCA5A5] bg-[#EF4444]/10 border border-[#EF4444]/30 rounded px-2 py-1.5 text-xs">
            {error}
          </div>
        )}
        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button" onClick={onClose} disabled={busy}
            className="text-xs px-3 py-2 rounded bg-zinc-800 border border-zinc-700 text-zinc-300 hover:text-white"
          >Cancel</button>
          <button
            type="submit" disabled={busy}
            data-testid="reset-password-submit"
            className="text-xs px-3 py-2 rounded bg-[#38BDF8] hover:bg-[#7DD3FC] text-black font-semibold disabled:opacity-50"
          >{busy ? "Saving…" : "Update password"}</button>
        </div>
      </form>
    </ModalShell>
  );
}


function ModalShell({ title, testid, onClose, children }) {
  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid={testid}
    >
      <div
        className="bg-[#141417] border border-zinc-800 rounded-xl w-full max-w-md shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-zinc-800 px-4 py-2.5">
          <h3 className="text-sm font-semibold text-white">{title}</h3>
          <button
            type="button" onClick={onClose}
            className="text-zinc-500 hover:text-white"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-4">{children}</div>
      </div>
      {/* Local style block keeps the input styling consistent without polluting Tailwind config */}
      <style>{`
        .modal-input {
          width: 100%;
          background: #0B0B0E;
          border: 1px solid #27272a;
          border-radius: 6px;
          padding: 8px 10px;
          font-size: 13px;
          color: #fff;
          outline: none;
          transition: border-color 0.15s;
        }
        .modal-input:focus { border-color: #D4A968; }
      `}</style>
    </div>
  );
}


function Field({ label, hint, required, children }) {
  return (
    <label className="block">
      <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 block mb-1">
        {label}{required && <span className="text-[#D4A968]"> *</span>}
      </span>
      {children}
      {hint && <span className="text-[10px] text-zinc-500 mt-0.5 block">{hint}</span>}
    </label>
  );
}


function PlanCard({ tier, title, tagline, features, users, highlighted }) {
  const count = (users || []).filter((u) => (u.tier || "basic") === tier).length;
  return (
    <div
      className={
        highlighted
          ? "bg-[#141417] border border-[#D4A968]/40 rounded-lg p-4 relative overflow-hidden"
          : "bg-[#141417] border border-zinc-800 rounded-lg p-4"
      }
      data-testid={`plan-card-${tier}`}
    >
      {highlighted && (
        <div className="absolute top-0 right-0 bg-[#D4A968] text-black text-[9px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-bl">
          Admin-Granted
        </div>
      )}
      <div className="flex items-center gap-2 mb-1">
        {highlighted
          ? <Crown className="w-4 h-4 text-[#D4A968]" />
          : <Layers className="w-4 h-4 text-zinc-400" />
        }
        <h2 className={`text-base font-semibold ${highlighted ? "text-[#D4A968]" : "text-white"}`}>
          {title}
        </h2>
        <span className="ml-auto text-[10px] font-mono uppercase tracking-widest text-zinc-500">
          {count} {count === 1 ? "user" : "users"}
        </span>
      </div>
      <p className="text-xs text-zinc-500 mb-3">{tagline}</p>
      <ul className="space-y-1.5">
        {features.map((f, i) => (
          <li key={i} className="flex items-start gap-2 text-xs text-zinc-300 leading-snug">
            <CheckCircle2 className={`w-3 h-3 mt-0.5 shrink-0 ${highlighted ? "text-[#D4A968]" : "text-zinc-500"}`} />
            <span>{f}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
